import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Button, ButtonProps } from '@abyss/web/ui/Button';
import { IconMaterial, IconMaterialProps } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { useParsedProviderFilters } from '../../hooks/useProviderFilterCount';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { Option, ProviderSearchFilters } from '../../models/ProviderSearch';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { compareSharePopoverStyles } from '../CompareShareToggle/CompareShareToggle.styled';
import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
  SectionType,
} from '../Constants';
import {
  mobileOnly,
  phoneOnly,
  viewFilterSortScreen,
} from '../ConstantsStyles';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import { AllFilterDrawer } from './AllFilterDrawer';

type Props = {
  name: string;
  size: string;
  color?: string;
  icon?: string;
  variant?: string;
  openMapview?: boolean;
  iconColor?: string;
  coverageTypeData?: string;
  providerFilterList?: ProviderSearchFilters;
  sectionType?: string;
};

const flattenOptions = (options: string[]): string[] =>
  options.flatMap((option) =>
    option.split(',').map((value: string) => value.trim())
  );

const isOptionAvailable = (
  selectedOptions: string[],
  flattenedOptions: string[]
): boolean => selectedOptions.some((value) => flattenedOptions.includes(value));

export const ResultFilter = ({
  name: filterName,
  size,
  color = '$primary1',
  iconColor = '$interactive1',
  icon = 'tune',
  variant = 'outline',
  openMapview = false,
  coverageTypeData,
  providerFilterList,
  sectionType,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCoachMarkWebDistance, setShowCoachMarkWebDistance] =
    useState(true);
  const [
    showCoachMarkWebUpcomingAvailability,
    setShowCoachMarkWebUpcomingAvailability,
  ] = useState(true);
  const [
    showCoachMarkWebPriorityScheduling,
    setShowCoachMarkWebPriorityScheduling,
  ] = useState(true);

  const { searchFilters, setSearchFilters, defaultSearchFilters } =
    useContext(SearchFilterContext);

  const [selectedFilters, setSelectedFilters] =
    useState<ProviderSearchFilters>(searchFilters);
  const { latitude, longitude } = getGeoLocationFromStorage();

  const { portalData } = useContext(PortalContext);
  const portalLockEAPFilter = portalData?.portalConfig?.lockEapFilter;

  const lockedPill = (option) =>
    portalLockEAPFilter && option?.dnt_label === EAP_OPTION_LABEL;

  const eapOnlyClientOption = selectedFilters[FilterCollectionModel.EAP_OFFERED]
    ? selectedFilters[FilterCollectionModel.EAP_OFFERED].filter((option) =>
        lockedPill(option)
      )
    : [];

  if (eapOnlyClientOption.length > 0) {
    localStorage.setItem(
      'eapOnlyClientOption',
      JSON.stringify(eapOnlyClientOption)
    );
  } else {
    const eapOnlyClientOption: Option[] = JSON.parse(
      localStorage.getItem('eapOnlyClientOption') ?? '[]'
    );
    if (eapOnlyClientOption) {
      localStorage.removeItem('eapOnlyClientOption');
    }
  }

  const [enableMapViewEnhancements] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
  ]);
  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements ? mobileOnly : phoneOnly
  );
  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);
  const filterSortScreen = useMediaQuery(viewFilterSortScreen);
  const isGuest = storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  // Check for enableBccFilter URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const enableBccSearchFromUrl = urlParams.get('enableBccSearch') === 'true';

  const [
    enhanceDistanceFilter,
    enableShowDefaultSearchRadiusPopoverMsg,
    enableShowUpcomingAvailabilityPopoverMsgForBH,
    enableShowPrioritySchedulingPopoverMsg,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_DEFAULT_SEARCH_RADIUS_POPOVER_MSG,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_UPCOMING_AVAILABILITY_POPOVER_MSG_FOR_BH,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_PRIORITY_SCHEDULING_POPOVER_MSG,
  ]);

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const defaultSearchRadiusMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.DEFAULT_SEARCH_RADIUS_MESSAGE,
  });

  const upcomingAvailabilityMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.UPCOMING_AVAILABILITY_MESSAGE,
  });

  const prioritySchedulingMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_COACHMARK_MESSAGE,
  });

  const distanceFilterMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.DISTANCE_FILTER_MAX_VALIDITY_DAYS,
  });
  const distanceMaxValidityDays = parseInt(
    distanceFilterMaxValidityDaysMessage?.message || '0',
    10
  );

  const upcomingAvailabilityMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING
      .UPCOMING_AVAILABILITY_FILTER_MAX_VALIDITY_DAYS,
  });
  const upcomingAvailabilityMaxValidityDays = parseInt(
    upcomingAvailabilityMaxValidityDaysMessage?.message || '0',
    10
  );

  const prioritySchedulingMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING
      .PRIORITY_SCHEDULING_FILTER_MAX_VALIDITY_DAYS,
  });
  const prioritySchedulingMaxValidityDays = parseInt(
    prioritySchedulingMaxValidityDaysMessage?.message || '0',
    10
  );

  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({});
  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({});
  const hsidUuid = useHsidUuid();

  const [showDistancePopover, setShowDistancePopover] = useState(false);
  const [showUpcomingAvailabilityPopover, setShowUpcomingAvailabilityPopover] =
    useState(false);
  const [showPrioritySchedulingPopover, setShowPrioritySchedulingPopover] =
    useState(false);

  const setDistanceFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_DISTANCE_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );
  const currentDate = new Date().toISOString().split('T')[0];
  const distanceFilterDontShowButtonClickedDate = useStore(
    StoreKeys.DISTANCE_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const upcomingAvailabilityFilterDontShowButtonClickedDate = useStore(
    StoreKeys.UPCOMING_AVAILABILITY_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const setUpcomingAvailabilityFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_UPCOMING_AVAILABILITY_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const prioritySchedulingFilterDontShowButtonClickedDate = useStore(
    StoreKeys.PRIORITY_SCHEDULING_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const setPrioritySchedulingFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_PRIORITY_SCHEDULING_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );
  const { t } = useTranslation();
  const { providerFilterList: filtersData } = useParsedProviderFilters({
    selectedFilters,
  });

  const resultFilterDataTestId: string = 'result-filter';

  const selectedFiltersCount = useMemo(() => {
    const visibleSelectedFilters = Object.keys(searchFilters)
      .map((model) => {
        if (Array.isArray(searchFilters[model])) {
          return searchFilters[model]
            .filter((option: Option) => !option.hide)
            .map((option: Option) => ({
              label: option.label,
              value: option.value,
              model,
            }));
        } else {
          const option: Option = searchFilters[model];
          if (option?.value && !option?.hide) {
            return [
              {
                label: option.label,
                value: option.value,
                model,
              },
            ];
          }
        }
        return [];
      })
      .flat();

    const selectedFiltersCount = visibleSelectedFilters.filter(
      ({ model, value }) => {
        if (model === FilterCollectionModel.DISTANCE) {
          return !enhanceDistanceFilter;
        }
        const availableOptions =
          filtersData[model]?.options.map(({ value }) => value) || [];
        const flattenedOptions = flattenOptions(availableOptions);
        const selectedOptions = value
          .toString()
          .split(',')
          .map((value) => value.trim());

        const isSelectedOptionAvailable = isOptionAvailable(
          selectedOptions,
          flattenedOptions
        );
        if (isSelectedOptionAvailable) {
          return true;
        }
        return false;
      }
    ).length;

    return selectedFiltersCount;
  }, [
    JSON.stringify(searchFilters),
    JSON.stringify(filtersData),
    enhanceDistanceFilter,
  ]);

  let properties: any = {
    buttonStyles: {
      backgroundColor:
        selectedFiltersCount > 0 && !enableMapViewEnhancements
          ? '$tint3'
          : '$primary2',
      border: '1px solid $gray4',
      padding: '$sm $lg $sm $md',
      height: '2.625em',
      fontWeight: '$semibold',
      borderRadius: '24px',
      color: '$gray8',
    },
    drawerSize: 492,
    drawerposition: 'right',
  };
  const findDifferenceInDays = (date1: string, date2: string): number => {
    const parsedDate1 = new Date(date1);
    const parsedDate2 = new Date(date2);

    const msInDay = 24 * 60 * 60 * 1000; // Milliseconds in a day
    return Math.floor(
      (parsedDate1.getTime() - parsedDate2.getTime()) / msInDay
    );
  };

  if (mobileScreen) {
    properties = {
      buttonStyles: {
        backgroundColor: selectedFiltersCount > 0 ? '$tint1' : '$primary2',
        boxShadow:
          selectedFiltersCount > 0 ? '0 2px rgba(25, 25, 26, 0.16)' : 'unset',
        border: 'solid 2px $primary1',
        '&:active, &:focus, &:hover': {
          backgroundColor: '$tint3',
        },
        whiteSpace: 'nowrap',
      },
      drawerSize: window.innerHeight - 15,
      drawerPosition: 'bottom',
    };
  }

  useEffect(() => {
    if (!isEqual(searchFilters, selectedFilters)) {
      setSelectedFilters({ ...searchFilters });
    }
  }, [searchFilters, latitude, longitude]);
  useEffect(() => {
    getEducationalContentStatus({
      variables: { isSuperUser, ...(hsidUuid && { userId: hsidUuid }) },
    });
  }, []);
  const handleOnClickResultFilter = () => {
    const linkName: string = 'all filters';
    const linkLocation: string = `body:${Constants.PROVIDER_RESULTS.FILTER_BY}`;
    const modalName: string = `${linkName} drawer`;
    handleLinkAndModalTrack(linkName, linkLocation, modalName);
    storage.session.set('allFiltersOpen', true);
    setIsOpen(true);
  };

  const allFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const handleCloseAllFilterDrawer = () => {
    storage.session.set('allFiltersOpen', false);
    setIsOpen(false);
    allFiltersButtonRef.current?.focus();
  };
  const filterMessageClosedForDistanceSessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION.FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE
    ) ?? '';

  const filterMessageClosedForUpcomingAvailabilitySessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION
        .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY
    ) ?? '';

  const filterMessageClosedForPrioritySchedulingSessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION
        .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING
    ) ?? '';

  const psCookie = storage.cookie.get(Constants.COOKIES.PLAN_SELECTOR);
  const sessionIdOBH = storage.cookie.get('session_id');
  const sessionIdUMR = storage.cookie
    .get('FINDCARE_AND_COST_SSO_SESSION')
    ?.split('!')
    ?.at(1);
  const guestSessionCookie = storage.cookie.get('PSX_GUEST_SESSION');
  const sessionKey =
    psCookie || sessionIdOBH || sessionIdUMR || guestSessionCookie || 'Close';
  const isDistanceCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForDistanceSessionValue === sessionKey;
  const isUpcomingAvailabilityCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForUpcomingAvailabilitySessionValue === sessionKey;
  const isPrioritySchedulingCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForPrioritySchedulingSessionValue === sessionKey;

  const isBH =
    coverageTypeData === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];
  const isPractitioner =
    sectionType?.toLowerCase() === SectionType.PROVIDER.toLowerCase();

  const handleOpenChangeDistance = (open: boolean) => {
    if (openMapview) {
      setShowCoachMarkWebDistance(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE,
        sessionKey
      );
    }
    if (!open) {
      const activeElementClasses = document.activeElement?.classList;
      const crossIconClicked = activeElementClasses?.contains(
        'abyss-popover-close-button'
      );
      if (crossIconClicked) {
        setShowCoachMarkWebDistance(false);
        storage.session.set(
          Constants.STORAGE_KEYS.SESSION
            .FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE,
          sessionKey
        );
      }
    }
  };

  const handleOpenChangeUpcomingAvailability = (open: boolean) => {
    if (openMapview) {
      setShowCoachMarkWebUpcomingAvailability(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY,
        sessionKey
      );
    }
    if (!open) {
      const activeElementClasses = document.activeElement?.classList;
      const crossIconClicked = activeElementClasses?.contains(
        'abyss-popover-close-button'
      );
      if (crossIconClicked) {
        setShowCoachMarkWebUpcomingAvailability(false);
        storage.session.set(
          Constants.STORAGE_KEYS.SESSION
            .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY,
          sessionKey
        );
      }
    }
  };

  const handleOpenChangePriorityScheduling = (open: boolean) => {
    if (openMapview) {
      setShowCoachMarkWebPriorityScheduling(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING,
        sessionKey
      );
    }
    if (!open) {
      const activeElementClasses = document.activeElement?.classList;
      const crossIconClicked = activeElementClasses?.contains(
        'abyss-popover-close-button'
      );
      if (crossIconClicked) {
        setShowCoachMarkWebPriorityScheduling(false);
        storage.session.set(
          Constants.STORAGE_KEYS.SESSION
            .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING,
          sessionKey
        );
      }
    }
  };

  useEffect(() => {
    if (providerFilterList && Object.keys(providerFilterList).length > 0) {
      const clickedDate =
        distanceFilterDontShowButtonClickedDate ||
        educationalContent?.data?.educationalContentStatus
          ?.distanceFilterBtnClickedDate ||
        '';

      if (!distanceFilterDontShowButtonClickedDate) {
        setDistanceFilterDontShowButtonClickedDate(clickedDate);
      }
      if (
        !providerFilterList?.UpcomingAvailability ||
        !(isBH && isPractitioner)
      ) {
        if (clickedDate === '') {
          setShowDistancePopover(true);
        } else {
          const difference = findDifferenceInDays(currentDate, clickedDate);
          setShowDistancePopover(difference > distanceMaxValidityDays);
        }
      }
    }
  }, [
    educationalContent?.data,
    distanceFilterDontShowButtonClickedDate,
    providerFilterList,
  ]);

  useEffect(() => {
    const clickedDate =
      upcomingAvailabilityFilterDontShowButtonClickedDate ||
      educationalContent?.data?.educationalContentStatus
        ?.upcomingAvailabilityFilterBtnClickedDate ||
      '';

    if (!upcomingAvailabilityFilterDontShowButtonClickedDate) {
      setUpcomingAvailabilityFilterDontShowButtonClickedDate(clickedDate);
    }

    if (clickedDate === '') {
      setShowUpcomingAvailabilityPopover(true);
    } else {
      const storedDate = clickedDate;
      const difference = findDifferenceInDays(currentDate, storedDate);
      setShowUpcomingAvailabilityPopover(
        difference > upcomingAvailabilityMaxValidityDays
      );
    }
  }, [
    educationalContent?.data,
    upcomingAvailabilityFilterDontShowButtonClickedDate,
  ]);

  useEffect(() => {
    const clickedDate =
      prioritySchedulingFilterDontShowButtonClickedDate ||
      educationalContent?.data?.educationalContentStatus
        ?.prioritySchedulingFilterBtnClickedDate ||
      '';

    if (!prioritySchedulingFilterDontShowButtonClickedDate) {
      setPrioritySchedulingFilterDontShowButtonClickedDate(clickedDate);
    }

    if (clickedDate === '') {
      setShowPrioritySchedulingPopover(true);
    } else {
      const storedDate = clickedDate;
      const difference = findDifferenceInDays(currentDate, storedDate);
      setShowPrioritySchedulingPopover(
        difference > prioritySchedulingMaxValidityDays
      );
    }
  }, [
    educationalContent?.data,
    prioritySchedulingFilterDontShowButtonClickedDate,
  ]);

  const renderFilterButton = () => (
    <ErrorBoundary>
      <Button
        css={{ 'abyss-button-root': { ...properties.buttonStyles } }}
        data-auto-testid={resultFilterDataTestId}
        data-testid={resultFilterDataTestId}
        onClick={handleOnClickResultFilter}
        ref={allFiltersButtonRef}
        size={size}
        variant={variant as ButtonProps['variant']}
      >
        <Layout.Group>
          <IconMaterial
            color={iconColor}
            icon={icon as IconMaterialProps['icon']}
          />
          <Text
            color={color}
            fontWeight={
              mobileScreen && !enableMapViewHideOption ? 600 : undefined
            }
          >{`${filterName} ${
            selectedFiltersCount ? ` (${selectedFiltersCount})` : ''
          }`}</Text>
        </Layout.Group>
      </Button>
    </ErrorBoundary>
  );
  const handleDontShowAgain = () => {
    setEducationalContentStatus({
      variables: {
        distanceFilterBtnClickedDate: currentDate,
        hasViewedContent:
          educationalContent?.data?.educationalContentStatus?.hasViewedContent,
        ...(hsidUuid && { userId: hsidUuid }),
        isSuperUser,
      },
    });
    setDistanceFilterDontShowButtonClickedDate(
      educationalContent?.data?.educationalContentStatus
        ?.distanceFilterBtnClickedDate || ''
    );
    setShowCoachMarkWebDistance(false);
  };

  const handleDontShowAgainUpcomingAvailability = () => {
    setEducationalContentStatus({
      variables: {
        upcomingAvailabilityFilterBtnClickedDate: currentDate,
        hasViewedContent:
          educationalContent?.data?.educationalContentStatus?.hasViewedContent,
        ...(hsidUuid && { userId: hsidUuid }),
        isSuperUser,
      },
    });
    setUpcomingAvailabilityFilterDontShowButtonClickedDate(
      educationalContent?.data?.educationalContentStatus
        ?.upcomingAvailabilityFilterBtnClickedDate || ''
    );
    setShowCoachMarkWebUpcomingAvailability(false);
  };

  const handleDontShowAgainPriorityScheduling = () => {
    setEducationalContentStatus({
      variables: {
        prioritySchedulingFilterBtnClickedDate: currentDate,
        hasViewedContent:
          educationalContent?.data?.educationalContentStatus?.hasViewedContent,
        ...(hsidUuid && { userId: hsidUuid }),
        isSuperUser,
      },
    });
    setPrioritySchedulingFilterDontShowButtonClickedDate(
      educationalContent?.data?.educationalContentStatus
        ?.prioritySchedulingFilterBtnClickedDate || ''
    );
    setShowCoachMarkWebPriorityScheduling(false);
  };

  const popOverContent = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {defaultSearchRadiusMessage?.message?.replace(
          '{{miles}}',
          searchRadius
        )}
      </p>
      {!isGuest && (
        <Button data-testid="popover-button" onClick={handleDontShowAgain}>
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const popOverContentUpcomingAvailability = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {upcomingAvailabilityMessage?.message}
      </p>
      {!isGuest && (
        <Button
          data-testid="popover-button"
          onClick={handleDontShowAgainUpcomingAvailability}
        >
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const popOverContentPriorityScheduling = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {prioritySchedulingMessage?.message}
      </p>
      {!isGuest && (
        <Button
          data-testid="popover-button"
          onClick={handleDontShowAgainPriorityScheduling}
        >
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const showPrioritySchedulingCoachmark =
    enableBccSearchFromUrl &&
    enableShowPrioritySchedulingPopoverMsg &&
    !isPrioritySchedulingCoachMarkClosed &&
    showPrioritySchedulingPopover;

  const showUpcomingAvailabilityCoachmark =
    !enableBccSearchFromUrl &&
    isBH &&
    isPractitioner &&
    enableShowUpcomingAvailabilityPopoverMsgForBH &&
    !isUpcomingAvailabilityCoachMarkClosed &&
    showUpcomingAvailabilityPopover;

  const showDistanceCoachMark =
    !enableBccSearchFromUrl &&
    enableShowDefaultSearchRadiusPopoverMsg &&
    !isDistanceCoachMarkClosed &&
    showDistancePopover;

  const renderCoachmarkPopover = () => {
    if (showPrioritySchedulingCoachmark) {
      return (
        <Popover
          align="center"
          content={popOverContentPriorityScheduling()}
          css={{
            ...compareSharePopoverStyles,
            '.abyss-button-root': {
              color: '#4B4D4F',
              backgroundColor: '#FFFFFF',
              border: '1px solid #4B4D4F',
            },
            'abyss-popover-close-button': {
              boxShadow: 'none !important',
            },
            '.abyss-icon-symbol': {
              '&:focus-visible': {
                boxShadow: 'none',
              },
            },
          }}
          data-testid="priority-scheduling-popover"
          onOpenChange={handleOpenChangePriorityScheduling}
          open={showCoachMarkWebPriorityScheduling && filterSortScreen}
          position="bottom"
          positionOffset={17}
          style={{ zIndex: 999 }}
          title={prioritySchedulingMessage?.title}
        >
          {renderFilterButton()}
        </Popover>
      );
    }
    if (showUpcomingAvailabilityCoachmark) {
      return (
        <Popover
          align="center"
          content={popOverContentUpcomingAvailability()}
          css={{
            ...compareSharePopoverStyles,
            '.abyss-button-root': {
              color: '#4B4D4F',
              backgroundColor: '#FFFFFF',
              border: '1px solid #4B4D4F',
            },
            'abyss-popover-close-button': {
              boxShadow: 'none !important',
            },
            '.abyss-icon-symbol': {
              '&:focus-visible': {
                boxShadow: 'none',
              },
            },
          }}
          data-testid="upcoming-availability-popover"
          onOpenChange={handleOpenChangeUpcomingAvailability}
          open={showCoachMarkWebUpcomingAvailability && filterSortScreen}
          position="bottom"
          positionOffset={17}
          style={{ zIndex: 999 }}
          title={upcomingAvailabilityMessage?.title}
        >
          {renderFilterButton()}
        </Popover>
      );
    }
    if (showDistanceCoachMark) {
      return (
        <Popover
          align="center"
          content={popOverContent()}
          css={{
            ...compareSharePopoverStyles,
            '.abyss-button-root': {
              color: '#4B4D4F',
              backgroundColor: '#FFFFFF',
              border: '1px solid #4B4D4F',
            },
            'abyss-popover-close-button': {
              boxShadow: 'none !important',
            },
            '.abyss-icon-symbol': {
              '&:focus-visible': {
                boxShadow: 'none',
              },
            },
          }}
          data-testid="popover"
          onOpenChange={handleOpenChangeDistance}
          open={showCoachMarkWebDistance && filterSortScreen}
          position="bottom"
          positionOffset={17}
          style={{ zIndex: 999 }}
          title={defaultSearchRadiusMessage?.title}
        >
          {renderFilterButton()}
        </Popover>
      );
    }
    return renderFilterButton();
  };

  return (
    <React.Fragment>
      {enableMapViewHideOption ? null : <div data-tesid="condt-failed"></div>}
      {isOpen ? (
        <AllFilterDrawer
          allFiltersButtonRef={allFiltersButtonRef}
          defaultSearchFilters={defaultSearchFilters}
          isOpen={isOpen}
          onClose={handleCloseAllFilterDrawer}
          position={properties.drawerPosition}
          selectedFilters={selectedFilters}
          setSearchFilters={setSearchFilters}
          setSelectedFilters={setSelectedFilters}
          size={properties.drawerSize}
        />
      ) : null}
      {renderCoachmarkPopover()}
    </React.Fragment>
  );
};
